export type RiskLevel = 'HIGH' | 'LOW' | 'UNKNOWN';

export interface RiskAssessment {
  groundRisk: RiskLevel;
  buildingRisk: RiskLevel;
  overallRisk: RiskLevel;
  canSave: boolean;
  message: string;
  heightData?: number;
  buildingReason?: string;
}

export class RiskUtils {
  /**
   * Determine ground risk level based on height data (similar to DropPointSuggestorV2)
   */
  static getGroundRiskLevel(height?: number | null): RiskLevel {
    if (height === null || height === undefined || isNaN(height)) {
      return 'UNKNOWN';
    }
    if (height >= 1.0) {
      return 'HIGH';    // Unsafe - obstacles detected
    } else {
      return 'LOW';     // Safe - ground level, suitable for drone landing
    }
  }

  /**
   * Determine building risk level from API response
   */
  static getBuildingRiskLevel(buildingRiskResponse?: { risk: 'HIGH' | 'LOW'; reason?: string } | null): RiskLevel {
    if (!buildingRiskResponse) {
      return 'UNKNOWN';
    }
    return buildingRiskResponse.risk;
  }

  /**
   * Check if a location can be saved based on risk level
   */
  static canSaveLocation(riskLevel: RiskLevel, cellId?: number): boolean {
    // Cell 5 (testing) bypasses all safety restrictions
    if (cellId === 5) {
      return true;
    }
    
    // For all other cells, only allow LOW risk locations
    return riskLevel === 'LOW';
  }

  /**
   * Get comprehensive risk assessment combining ground and building risks
   */
  static getComprehensiveRiskAssessment(
    heightData: number | null,
    buildingRiskResponse: { risk: 'HIGH' | 'LOW'; reason?: string } | null,
    cellId?: number
  ): RiskAssessment {
    const groundRisk = this.getGroundRiskLevel(heightData);
    const buildingRisk = this.getBuildingRiskLevel(buildingRiskResponse);
    
    // Overall risk is HIGH if either ground or building risk is HIGH
    const overallRisk: RiskLevel = (groundRisk === 'HIGH' || buildingRisk === 'HIGH') 
      ? 'HIGH' 
      : (groundRisk === 'LOW' && buildingRisk === 'LOW') 
        ? 'LOW' 
        : 'UNKNOWN';

    // Allow saving if EITHER check passes (ground OR building is LOW) or if testing cell
    const canSave = cellId === 5 || groundRisk === 'LOW' || buildingRisk === 'LOW';
    const isTestingCell = cellId === 5;

    let message = '';
    if (isTestingCell) {
      message = `TEST MODE: All restrictions bypassed (Ground: ${heightData?.toFixed(1) || 'unknown'}m, Building: ${buildingRisk.toLowerCase()})`;
    } else if (canSave) {
      if (groundRisk === 'LOW' && buildingRisk === 'LOW') {
        message = `Safe to save (Ground: ${heightData?.toFixed(1)}m, Building: Clear)`;
      } else if (groundRisk === 'LOW') {
        message = `Can save: Ground clear (${heightData?.toFixed(1)}m), Building: ${buildingRisk.toLowerCase()}`;
      } else if (buildingRisk === 'LOW') {
        message = `Can save: Building clear, Ground: ${groundRisk.toLowerCase()} (${heightData?.toFixed(1) || 'unknown'}m)`;
      }
    } else {
      // Both checks failed or unknown
      if (groundRisk === 'HIGH' && buildingRisk === 'HIGH') {
        message = `Cannot save: Both ground obstacles (≥1.0m) and building proximity detected`;
      } else if (groundRisk === 'UNKNOWN' && buildingRisk === 'UNKNOWN') {
        message = 'Risk assessment incomplete - both ground and building data missing';
      } else if (groundRisk === 'UNKNOWN') {
        message = 'Risk assessment incomplete - ground height data missing';
      } else if (buildingRisk === 'UNKNOWN') {
        message = 'Risk assessment incomplete - building risk data missing';
      } else {
        message = `Cannot save: No passing criteria met`;
      }
    }

    return {
      groundRisk,
      buildingRisk,
      overallRisk,
      canSave,
      message,
      heightData: heightData || undefined,
      buildingReason: buildingRiskResponse?.reason,
    };
  }

  /**
   * Get display color for risk level
   */
  static getRiskColor(riskLevel: RiskLevel): string {
    switch (riskLevel) {
      case 'HIGH':
        return 'text-red-600';
      case 'LOW':
        return 'text-green-600';
      case 'UNKNOWN':
      default:
        return 'text-gray-500';
    }
  }

  /**
   * Get display emoji for risk level
   */
  static getRiskEmoji(riskLevel: RiskLevel): string {
    switch (riskLevel) {
      case 'HIGH':
        return '🔴';
      case 'LOW':
        return '🟢';
      case 'UNKNOWN':
      default:
        return '⚪';
    }
  }

  /**
   * Get risk status text for display
   */
  static getRiskStatusText(riskLevel: RiskLevel, heightData?: number, reason?: string): string {
    switch (riskLevel) {
      case 'HIGH':
        if (heightData !== undefined && heightData >= 1.0) {
          return `Unsafe (≥ 1.0m obstacles detected)`;
        } else if (reason) {
          return `High risk (${reason})`;
        } else {
          return 'High risk';
        }
      case 'LOW':
        if (heightData !== undefined) {
          return `Safe (${heightData.toFixed(1)}m height)`;
        } else {
          return 'Low risk';
        }
      case 'UNKNOWN':
      default:
        return 'Unknown risk';
    }
  }
}
